import React, { useState, useLayoutEffect } from "react";
import moment from "moment";

import { useModal } from "../../../../providers/ModalProvider/ModalProvider";

import {
  formatDateTime,
  formatTicketGroups,
  getSinglarOrPluralWord,
  getTimezoneDate,
  isPlural,
} from "../../../../utilities/helpers";

import { exchangeTickets } from "../../../../utilities/api";

import Stack from "react-bootstrap/Stack";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Modal from "react-bootstrap/Modal";

import { Spinner } from "../../../LoadingContainer/Spinner";
import { ExchangeSuccess } from "./ExchangeSuccess";
import { WarningCard } from "../../../WarningCard";

export default function ExchangeTickets({
  event,
  fromEventName,
  fromEventDate,
  fromEmail,
  selectedSeatIds,
  fromTickets,
}) {
  // Provider
  const { openModal, closeModal } = useModal();

  const [isExchanging, setIsExchanging] = useState(false);

  const [alert, setAlert] = useState({
    show: false,
    variant: "danger",
    message: "",
  });

  useLayoutEffect(() => {
    const mapModal = document.getElementById("exchange-map-modal");
    mapModal.classList.add("modal-opac");

    return () => {
      mapModal.classList.remove("modal-opac");
    };
  }, []);

  const getSeats = () => {
    const mapping = event?.seatmap?.mapping;
    const seats = selectedSeatIds?.seats;
    const GASections = selectedSeatIds?.gaSeats;
    if (seats) {
      const seatObjects = Object.keys(seats).map((seatId) => {
        const fullSeatObject = mapping.seats[seatId];
        return fullSeatObject;
      });
      return seatObjects;
    }
    if (GASections) {
      const sectionObjects = Object.entries(GASections).map(([key, value]) => {
        const sectionObject = mapping.sections[key];
        return {
          ...sectionObject,
          generalAdmission: true,
          selectedSeats: value,
        };
      });
      return sectionObjects;
    }
  };

  const handleExchange = () => {
    setIsExchanging(true);
    let ticketsFromMap = formatTicketGroups(getSeats());
    // ticketsFromMap = ticketsFromMap[0]?.tickets.map(ticket => ticket.seatId)
    let data = {};
    data["email"] = fromEmail;
    data["selectedTickets"] = fromTickets;
    data["selectedSeatIds"] = selectedSeatIds;
    data["eventUUID"] = event.uuid;
    exchangeTickets(data)
      .then((res) => {
        setIsExchanging(false);
        closeModal();
        if (!isExchanging) openSuccess(getSeats());
      })
      .catch((err) => {
        setIsExchanging(false);
        setAlert({
          show: true,
          variant: "danger",
          message:
            "Something went wrong. Unable to exchange tickets. Please try again",
        });
      });
  };

  const openSuccess = (tickets) => {
    openModal(
      ExchangeSuccess,
      {
        email: fromEmail,
        tickets,
      },
      {
        className: "modal--lg",
        contentClassName: "",
      },
      { replace: false },
    );
  };

  const getItem = (group, idx, type) => {
    let numGA;
    if (type === "exchange") numGA = group?.ticketCount;
    else numGA = group?.tickets[0]?.selectedSeats?.length;
    // if group is GA
    if (group?.generalAdmission) {
      // Create one element per selected seat
      return [...Array(numGA)].map((_, idx) => (
        <Stack
          as="li"
          direction="horizontal"
          className="small mt-1"
          gap={3}
          key={idx}
        >
          <Stack key={idx} direction="horizontal" gap={2}>
            <span className="text-muted fw-normal">Sec</span>
            <span>{group?.sectionNumber}</span>
          </Stack>
        </Stack>
      ));
    } else {
      return (
        <Stack
          as="li"
          direction="horizontal"
          className="small mt-1"
          gap={3}
          key={idx}
        >
          <Stack direction="horizontal" gap={2}>
            <span className="text-muted fw-normal">Sec</span>
            <span>{group?.sectionNumber}</span>
          </Stack>
          <Stack direction="horizontal" gap={2}>
            <span className="text-muted fw-normal">Row</span>
            <span>{group?.rowNumber}</span>
          </Stack>
          <Stack direction="horizontal" gap={2}>
            <span className="text-muted fw-normal">Seats</span>
            <span>{group?.seats}</span>
          </Stack>
        </Stack>
      );
    }
  };
  return (
    <Modal.Body>
      {alert.show && (
        <>
          <Alert variant={alert.variant} className="mb-4">
            <p>{alert.message}</p>
          </Alert>
        </>
      )}
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title">
          Are you sure you want to exchange{" "}
          {isPlural(fromTickets?.length) ? "these" : "this"}{" "}
          {getSinglarOrPluralWord(fromTickets?.length).toLowerCase()}?
        </h1>
      </div>
      <p>
        Once you exchange {isPlural(fromTickets?.length) ? "these" : "this"}{" "}
        {getSinglarOrPluralWord(fromTickets?.length).toLowerCase()}, the action
        cannot be undone.
      </p>
      <Stack gap={4} as="ul" className="mt-4">
        <li className="d-flex-column gap-1">
          <small className="small-bold text-muted">Exchange:</small>
          <ul className="normal-bold">
            <li>
              <span className="fw-bold">{fromEventName}</span>
            </li>
            <li>
              <span className="small fw-normal">
                {formatDateTime(
                  getTimezoneDate(fromEventDate, event?.timezone),
                )}
              </span>
            </li>
            {formatTicketGroups(fromTickets)?.map((ticketGroup, index) => (
              <>{getItem(ticketGroup, index, "exchange")}</>
            ))}
          </ul>
        </li>
        <li className="d-flex-column gap-1">
          <small className="small-bold text-muted">For:</small>
          <ul className="normal-bold">
            <li>
              <span className="fw-bold">{event?.name}</span>
            </li>
            <li>
              <span className="small fw-normal">
                {formatDateTime(getTimezoneDate(event.start, event?.timezone))}
              </span>
            </li>
            {formatTicketGroups(getSeats())?.map((ticketGroup, idx) => (
              <>{getItem(ticketGroup, idx, "to")}</>
            ))}
          </ul>
        </li>
      </Stack>
      <WarningCard
        text={`The ${getSinglarOrPluralWord(getSeats()?.length).toLowerCase()} will be updated in the user's profile and sent to ${fromEmail}`}
        variant="primary"
        margin="mt"
      />
      <Stack className="btn-group-flex">
        <Button variant="outline-light" size="lg" onClick={closeModal}>
          Cancel
        </Button>
        <Button size="lg" onClick={handleExchange} disabled={isExchanging}>
          {isExchanging ? (
            <Spinner />
          ) : (
            `Exchange ${getSinglarOrPluralWord(getSeats()?.length).toLowerCase()}`
          )}
        </Button>
      </Stack>
    </Modal.Body>
  );
}
